import { z } from 'zod';
import { apiEnvelope } from '../envelope';

/**
 * The member's emergency contact and emergency clinic, read from the Pelago 1.0
 * mirrors (ADR-138) on its OWN endpoint.
 *
 * A mirror only ever holds what migration brought over, and mirrors have no
 * application write path by design. So a member who registered natively in
 * Galapagos always reads empty here — and that is NOT the same fact as "this
 * member has no emergency contact". This surface is one a care person may reach
 * for during an escalation, so each block reports WHICH of several different
 * facts its emptiness represents rather than rendering one indistinguishable
 * blank.
 */

/**
 * Why a block is empty, or that it is not.
 *
 * Resolved in a fixed order, first match wins (the conditions overlap, so the
 * order is part of the contract):
 *
 *   1. no member-migration marker            -> `not_collected`
 *   2. >= 1 row with a displayable value     -> `recorded`
 *   3. the block's Tier 2 step is settled    -> `none_recorded`
 *   4. otherwise                             -> `import_pending`
 *
 * Rule 2 sits above rule 3 deliberately: data outranks the ledger. If rows are
 * present they are shown whatever the step ledger says, because withholding
 * data we hold in order to honour a bookkeeping status is the worst outcome
 * here. Rule 1 sits at the top because the marker is the only authority for
 * "was this member in 1.0 at all", and a member with no marker cannot
 * meaningfully be `none_recorded`.
 */
export const EMERGENCY_BLOCK_STATES = [
  /** 1.0 held this, and the values are in the block's array. */
  'recorded',
  /** The member was migrated, the import ran, and 1.0 held nothing. */
  'none_recorded',
  /** The member was migrated, but this block has not been imported yet. */
  'import_pending',
  /** The member never existed in 1.0, and there is no way to collect this yet. */
  'not_collected',
] as const;

export type EmergencyBlockState = (typeof EMERGENCY_BLOCK_STATES)[number];

/**
 * The cannot-say state, and it is CLIENT-ONLY — deliberately not a member of
 * the wire vocabulary above.
 *
 * It used to be `import_pending`, which looked like a cannot-say value and is
 * not one: it asserts that the member was migrated and this block's import has
 * not run. Resolving an unrecognised state to it made a newer server's state
 * indistinguishable from that claim, so an old client meeting a new vocabulary
 * would tell a care person the import had not happened — a statement about the
 * member, invented from a value the client could not read. Every renderer that
 * wants to say nothing now has a state that says nothing.
 */
export const EMERGENCY_BLOCK_STATE_UNRECOGNISED = 'unrecognised';

/**
 * What a CLIENT may hold: the wire vocabulary plus the cannot-say state the
 * tolerant read resolves to. The server never produces the extra member, which
 * is why `EmergencyBlockState` stays exactly the wire union.
 */
export type EmergencyBlockClientState =
  | EmergencyBlockState
  | typeof EMERGENCY_BLOCK_STATE_UNRECOGNISED;

/**
 * The WIRE contract: exactly the states the server emits.
 *
 * Strict on purpose. The OpenAPI document generated from this describes what
 * the server produces, and the server builds this value from a closed union —
 * so documenting it as "one of these four" is simply true, and widening it
 * would document a laxity that does not exist.
 *
 * Forward tolerance is a CLIENT concern and lives in the client schema below.
 * ADR-135's hazard is an old client meeting a new server, and its Rule 1 names
 * the client-side tolerant read as the idiom for exactly that.
 */
export const emergencyBlockStateSchema = z.enum(EMERGENCY_BLOCK_STATES);

/**
 * The CLIENT contract: the same shape, read forward-tolerantly.
 *
 * `.strict()` is not the relevant lever for this — it governs unknown object
 * KEYS, while an unknown enum MEMBER is rejected by the enum itself, and
 * ADR-135 Rule 3 classes an unhandled enum value as a breaking response change.
 * The native-model slice that repoints this surface is expected to add a state,
 * so a client built today must already survive meeting one: an unrecognised
 * value resolves to the cannot-say state rather than throwing, which fails in
 * the safe direction on a surface a care person may reach for in a crisis.
 *
 * Derived from the SAME `EMERGENCY_BLOCK_STATES` constant as the wire schema,
 * so the two cannot drift on the vocabulary — only on how they treat a value
 * outside it, which is the whole point.
 *
 * A client parsing through the wire schema above instead of this one loses that
 * tolerance silently, so the Atlas service that consumes it is pinned by test.
 *
 * The `z.string()` in front of the transform is what keeps the tolerance
 * narrow. Were the string check dropped — or the transform written as a bare
 * `.catch()` — ANY input would be absorbed, so a missing field, a `null` or a
 * number, which are a malformed payload rather than a newer vocabulary, would
 * resolve to the cannot-say state too. Rendering that is still a statement
 * about the member derived from a broken response, and the consuming service's
 * contract is the opposite: a malformed 200 throws so the card shows an error.
 * Forward tolerance is for a state this build has not heard of, never for a
 * payload that is not a state at all.
 */
const isWireBlockState = (value: string): value is EmergencyBlockState =>
  EMERGENCY_BLOCK_STATES.some((state) => state === value);

export const emergencyBlockStateClientSchema = z
  .string()
  .transform(
    (value): EmergencyBlockClientState =>
      isWireBlockState(value) ? value : EMERGENCY_BLOCK_STATE_UNRECOGNISED,
  );

/**
 * One mirrored emergency contact — a person.
 *
 * Every field is nullable because every mirrored column is: a mirror copies
 * what 1.0 delivered and invents no defaults (ADR-138 Rule 2). A null is
 * preserved as `null` and never coerced to `''`, so a reader can tell "1.0 held
 * no relationship" from "1.0 held an empty string".
 */
export const emergencyContactRowSchema = z.object({
  name: z.string().nullable(),
  relationship: z.string().nullable(),
  telephoneNumber: z.string().nullable(),
});

export type EmergencyContactRow = z.infer<typeof emergencyContactRowSchema>;

/**
 * One mirrored emergency clinic — a place. 1.0 calls the table
 * `emergency_surgery_detail`, "surgery" in the British sense of a GP practice.
 *
 * The address is carried as its structured fields, never pre-joined into a
 * display string: composing lines is presentation, and the client already
 * composes the member's own addresses the same way.
 */
export const emergencyClinicRowSchema = z.object({
  name: z.string().nullable(),
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  addressLine3: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  country: z.string().nullable(),
  postalCode: z.string().nullable(),
  telephoneNumber: z.string().nullable(),
});

export type EmergencyClinicRow = z.infer<typeof emergencyClinicRowSchema>;

/**
 * Both blocks carry a LIST, not a single record.
 *
 * `legacy_emergency_surgery_detail` is unique on `legacy_id` and merely indexed
 * on `member_id`, so a member can legitimately hold more than one clinic row;
 * `legacy_emergency_contact_details` is unique on `identity_id`, which bounds it
 * at one row per identity but not per member. Truncating a member's emergency
 * information to "the first one" on this surface is silent data loss that the
 * reader cannot detect, so every row is carried and every row is rendered.
 *
 * The array is always present — empty rather than absent — so no consumer has
 * to distinguish absent from empty.
 */
export const emergencyContactBlockSchema = z.object({
  state: emergencyBlockStateSchema,
  /** Ordered by the mirror's `identity_id`, ascending. */
  contacts: z.array(emergencyContactRowSchema),
  currentId: z.string().uuid().nullable().optional(),
});

export type EmergencyContactBlock = z.infer<typeof emergencyContactBlockSchema>;

export const emergencyClinicBlockSchema = z.object({
  state: emergencyBlockStateSchema,
  /** Ordered by the mirror's `legacy_id`, ascending. */
  clinics: z.array(emergencyClinicRowSchema),
  currentId: z.string().uuid().nullable().optional(),
});

export type EmergencyClinicBlock = z.infer<typeof emergencyClinicBlockSchema>;

/**
 * The two blocks resolve INDEPENDENTLY: they are separate 1.0 tables imported
 * by separate Tier 2 steps, so a member whose contact import has settled while
 * the surgery import has not reports two different states in one payload.
 */
export const memberEmergencyDetailsDataSchema = z.object({
  contact: emergencyContactBlockSchema,
  clinic: emergencyClinicBlockSchema,
});

export type MemberEmergencyDetailsData = z.infer<typeof memberEmergencyDetailsDataSchema>;

export const memberEmergencyDetailsResponseSchema = apiEnvelope(memberEmergencyDetailsDataSchema);

export type MemberEmergencyDetailsResponse = z.infer<typeof memberEmergencyDetailsResponseSchema>;

/**
 * The client-side parse of the same response — identical but for the
 * forward-tolerant state. Atlas reads through this; the server validates its
 * own output through the strict pair above.
 *
 * DERIVED from the wire blocks rather than re-declared, so "identical but for
 * the state" is structural instead of a promise two literals have to keep. A
 * second copy would strip any field added to a wire block before Atlas ever
 * saw it — a silent narrowing, invisible to every test that only reads the
 * fields it already knew about.
 */
export const memberEmergencyDetailsClientDataSchema = z.object({
  contact: emergencyContactBlockSchema.extend({ state: emergencyBlockStateClientSchema }),
  clinic: emergencyClinicBlockSchema.extend({ state: emergencyBlockStateClientSchema }),
});

export const memberEmergencyDetailsClientResponseSchema = apiEnvelope(
  memberEmergencyDetailsClientDataSchema,
);

export type MemberEmergencyDetailsClientData = z.infer<
  typeof memberEmergencyDetailsClientDataSchema
>;
